import { getWorkflowApps, WorkflowApp } from 'api/wfApi';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { UnauthorizedError } from 'utils/error';
import { useLoginContext } from './loginContext';

interface WorkflowAppsState {
  workflowApps: WorkflowApp[];
  loading: boolean;
  error: any;
}

interface WorkflowAppsFunction {
  getWorkflowApps: () => Promise<void>;
}

const initialState: WorkflowAppsState = {
  workflowApps: [] as WorkflowApp[],
  loading: false,
  error: undefined,
};

const initialFunction: WorkflowAppsFunction = {
  getWorkflowApps: async () => {},
};

type Action =
  | { type: 'loading' }
  | { type: 'synced'; payload: WorkflowApp[] }
  | { type: 'error'; payload: any }
  | { type: 'authError'; payload: any };

const reducer = (
  state: WorkflowAppsState,
  action: Action
): WorkflowAppsState => {
  switch (action.type) {
    case 'loading':
      return { ...state, error: undefined, loading: true };
    case 'synced':
      return { ...state, loading: false, workflowApps: action.payload };
    case 'error':
      return { ...state, loading: false, error: action.payload };
    case 'authError':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

export const WorkflowAppsContext = createContext<
  WorkflowAppsState & WorkflowAppsFunction
>({
  ...initialState,
  ...initialFunction,
});

WorkflowAppsContext.displayName = 'WorkflowAppsContext';

interface WorkflowAppsContextProviderProps {
  children: ReactNode;
}
export const WorkflowAppsContextProvider: React.FC<
  WorkflowAppsContextProviderProps
> = (props) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const loginContext = useLoginContext();

  const handleGetWorkflowApps = useCallback(async () => {
    try {
      if (state.loading) return;
      dispatch({ type: 'loading' });
      const getWorkflowAppsResult = await getWorkflowApps(500, 1, loginContext);
      dispatch({ type: 'synced', payload: getWorkflowAppsResult.results });
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        dispatch({ type: 'authError', payload: e });
        return;
      }
      dispatch({ type: 'error', payload: e });
    }
  }, [dispatch, state, loginContext]);

  const contextValue = useMemo(() => {
    return {
      ...state,
      getWorkflowApps: handleGetWorkflowApps,
    };
  }, [state, handleGetWorkflowApps]);

  return <WorkflowAppsContext.Provider value={contextValue} {...props} />;
};

export const useWorkflowAppsContext = () => {
  return useContext(WorkflowAppsContext);
};
